<template>
  <el-dialog
    v-model="localVisible"
    title="Новая запись"
    @close="onClose"
    :close-on-press-escape="false"
    style="max-width: 520px"
  >
    <div class="appointment-header-visual">
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <Calendar />
          </el-icon>
          <span>{{ form.date }}</span>
          <span style="color: #888;">
            ({{ formattedDate }}
            <span
              class="date-weekday"
              :class="{ weekend: isWeekend }"
            >
            {{ weekdayLabel }}
          </span>)
          </span>
        </div>
        <div class="header-item">
          <el-icon>
            <Clock />
          </el-icon>
          <span>{{ form.time }}</span>
        </div>
      </div>
      <div class="header-row">
        <div class="header-item">
          <el-icon>
            <UserFilled />
          </el-icon>
          <span>{{ form.employee.user_name }}</span>
        </div>
        <div class="header-item">
          <el-icon>
            <OfficeBuilding />
          </el-icon>
          <span> {{ form.employee.room_name }}</span>
        </div>
      </div>
    </div>


    <el-form :model="form" label-width="110px">

      <!-- Услуги (мультивыбор) -->
      <el-form-item label="Услуга">
        <ServiceMultiSelect
          v-model="form.services"
          :services="services"
          :remote="true"
          :city-id="form.employee?.city_id"
        />
      </el-form-item>

      <!-- Длительность -->
      <el-form-item label="Длительность">
        <el-input-number
          v-model="form.duration"
          :min="0"
          :step="15"
        />
        <span style="margin-left: 6px;">мин.</span>
      </el-form-item>

      <!-- Клиент -->
      <el-form-item label="Клиент">
        <el-autocomplete
          v-model="clientSearch"
          :fetch-suggestions="searchClientsFn"
          placeholder="Телефон или имя"
          @select="selectClient"
          @change="onClientInput"
          :trigger-on-focus="false"
          style="width: 100%;"
        >
          <template #default="{ item }">
            <div v-if="item.create" class="autocomplete-create">
              <el-icon>
                <Plus />
              </el-icon>
              <span>Создать клиента: <b>{{ item.value }}</b></span>
            </div>
            <div v-else class="autocomplete-client-item">
              <div class="client-row">
                <el-icon>
                  <UserFilled />
                </el-icon>
                <span>{{ item.fio }}</span>
              </div>
              <div class="client-row" v-if="item.phone">
                <el-icon>
                  <Phone />
                </el-icon>
                <span>{{ item.phone }}</span>
              </div>
            </div>
          </template>
        </el-autocomplete>
        <el-card class="client-selected-block" v-if="form.client">
          <div class="client-row">
            <el-input :prefix-icon="PhoneFilled" v-model="form.client.phone" disabled />
          </div>
          <div class="client-row">
            <el-input :prefix-icon="User" v-model="form.client.fio" disabled />
          </div>
          <el-link @click="form.client = null" type="danger" class="client-selected-reset">Сбросить</el-link>
        </el-card>
      </el-form-item>

      <!-- Комментарий -->
      <el-form-item label="Комментарий">
        <el-input
          type="textarea"
          v-model="form.comment"
          placeholder="Комментарий"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="onClose">Отмена</el-button>
      <el-button type="primary" @click="save" :disabled="!canSave">Записать</el-button>
    </template>

    <!-- Быстрое создание клиента -->
    <QuickClientCreate
      :visible="showQuickCreate"
      :city-id="form.employee.city_id"
      :default-fio="quickCreateForm.fio"
      :default-phone="quickCreateForm.phone"
      @save="handleQuickCreateClient"
      @close="showQuickCreate = false"
    />
  </el-dialog>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import QuickClientCreate from '@/components/util/calendar/QuickClientCreate.vue'
import { Calendar, Clock, OfficeBuilding, Phone, PhoneFilled, Plus, User, UserFilled } from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'

export default {
  name: 'AppointmentCreateModal',
  components: { Plus, Phone, ServiceMultiSelect, Clock, UserFilled, OfficeBuilding, QuickClientCreate, Calendar },
  props: {
    visible: { type: Boolean, required: true },
    date: { type: String, required: true },
    time: { type: String, required: true, default: '' },
    employee: { type: Object, required: true }
  },
  data() {
    return {
      quickCreateForm: {
        fio: '',
        phone: ''
      },
      localVisible: this.visible,
      form: {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        serviceIds: [],
        duration: 0,
        client: null,
        comment: ''
      },
      serviceSearch: '',
      clientSearch: '',
      showQuickCreate: false,
      lastSuggestions: [],
      showCreateClientButton: false
    }
  },
  computed: {
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return ''
      const weekdays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']
      return weekdays[d.getDay()]
    },
    isWeekend() {
      if (!this.date) return false
      const d = new Date(this.date)
      if (isNaN(d.getTime())) return false
      return d.getDay() === 0 || d.getDay() === 6
    },
    User() {
      return User
    },
    PhoneFilled() {
      return PhoneFilled
    },
    ...mapState('clients', ['search_clients']),
    ...mapState('services', ['services']),
    canSave() {
      return this.form.services.length > 0 && this.form.client
    },
    totalDuration() {
      return this.form.services.reduce((sum, s) => sum + (+s.duration || 0), 0)
    },
    formattedDate() {
      // Если дата уже Date-объект — пропусти этот шаг
      const months = [
        'Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня',
        'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'
      ]
      // Ожидается формат 'YYYY-MM-DD'
      const [ , month, day] = this.form.date.split('-')
      const monthName = months[parseInt(month, 10) - 1]
      return `${day} ${monthName}`
    }
  },
  watch: {
    visible(val) {
      this.localVisible = val
      if (val) this.resetForm()
    },
    localVisible(val) {
      if (!val) this.$emit('update:visible', false)
    },
    date(val) {
      this.form.date = val
    },
    time(val) {
      this.form.time = val
    },
    employee(val) {
      this.form.employee = val
    },
    'form.services': {
      handler() {
        // Синхронизируем длительность по выбранным услугам
        this.form.duration = this.totalDuration
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    ...mapActions('clients', ['searchClients', 'saveClient']),
    async searchClientsFn(query, cb) {
      if (!query || query.length < 3) return cb([])

      await this.searchClients({ query })
      let results = (this.search_clients || []).map(c => ({
        ...c,
        value: c.fio + (c.phone ? ` (${c.phone})` : '')
      }))

      // Сохраняем результаты для корректной логики onClientInput
      this.lastSuggestions = results

      if (results.length === 0) {
        results = [{ value: query, create: true }]
      }

      cb(results)
    },
    onClientInput(val) {
      this.showCreateClientButton = val && val.length >= 3 && this.lastSuggestions.length === 0
    },
    selectClient(item) {
      if (item.create) {
        this.openQuickCreateClient(this.clientSearch)
      } else {
        this.form.client = item
        this.clientSearch = ''
        this.showCreateClientButton = false
      }
    },
    openQuickCreateClient(query) {
      let phone = ''
      let fio = ''
      if (/^\d[\d\-+()\s]+$/.test(query)) phone = query
      else fio = query
      this.quickCreateForm = { fio, phone }
      this.showQuickCreate = true
    },
    async handleQuickCreateClient(payload) {
      const res = await this.$store.dispatch('clients/saveQuickClient', payload);

      console.debug(res)

      const newClient = res && res.id
        ? { id: res.id, fio: payload.fio, phone: payload.phone }
        : { fio: payload.fio, phone: payload.phone }
      this.form.client = newClient
      this.clientSearch = newClient.fio
      this.showQuickCreate = false
      this.showCreateClientButton = false
    },
    resetForm() {
      this.form = {
        date: this.date,
        time: this.time,
        employee: this.employee,
        services: [],
        duration: 0,
        client: null,
        comment: ''
      }
      this.clientSearch = ''
      this.showCreateClientButton = false
      this.lastSuggestions = []
    },
    onClose() {
      this.localVisible = false
    },
    async save() {
      // Считаем суммарную длительность
      const totalDuration = (this.form.services || []).reduce((sum, srv) => sum + (+srv.duration || 0), 0)

      // Считаем время окончания
      function addMinutes(time, min) {
        let [h, m] = time.split(':').map(Number)
        m += min
        h += Math.floor(m / 60)
        m = m % 60
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      }

      const endTime = addMinutes(this.form.time, totalDuration)

      const appointment = {
        id: this.form.id || null,
        branch_id: this.form.employee.branch_id,
        city_id: this.form.employee.city_id,
        room_id: this.form.employee.room_id,
        user_id: this.form.employee.user_id,
        user_name: this.form.employee.user_name,
        client_id: this.form.client.id,
        client_name: this.form.client.fio,
        client_phone: this.form.client.phone,
        date: this.form.date,
        start_time: this.form.time,
        end_time: endTime,
        services: this.form.services,
        comment: this.form.comment,
        duration_time: totalDuration,
        status: 'future', // или другой статус, если нужен
        color: '#3276e4'  // если нужен для UI
      }
      await this.$store.dispatch('appointments/saveAppointment', appointment)
      this.onClose();
    }
  }
}
</script>
<style scoped>
.date-weekday {
  font-size: 15px;
  color: #5a8fef;
  margin-bottom: 3px;
  font-weight: 600;
}
.date-weekday.weekend {
  color: #e53935;
  font-weight: 700;
}
.appointment-header-visual {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.header-row {
  display: flex;
  gap: 32px;
  align-items: center;
  justify-content: space-between;
}

.header-item {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #454545;
  gap: 8px;
}

.client-block {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 15px;
}

.autocomplete-create {
  display: flex;
  align-items: center;
  color: #3276e4;
  gap: 8px;
  font-size: 15px;
  padding: 2px 0;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.client-selected-block {
  margin-top: 12px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 10px #0000000a;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.client-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  color: #444;
  margin-bottom: 10px;
}

.client-selected-reset {
  align-self: flex-end;
  margin-top: 10px;
}
</style>
